import React, { useState, useEffect } from "react";
import {QueryClient, QueryClientProvider} from "react-query";
import {useLocation, useNavigate} from "react-router-dom";
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import {SystemRoutes} from "../../../Utils/RouteUtils";
import {HeaderMenu} from "../HeaderMenu";
import TicketFields from "./components/TicketFields";
import TicketAlarms from './components/TicketAlarms';

import {Ticket} from "../../../Shared/Types/Ticket";
import {
    OpenTicket,
    UpdateTicket,
    FindByIdTicket,
    FindTicketAlarms
} from "./TicketReactQuery";
import {Alarm} from "../../Dashboard/Alarms/AlarmReducer";
import { useAppSelector } from "../../../Config/Hooks";
import { hasPermission } from "../../../Shared/Auth/AuthenticationUtil";
import { AUTHORITIES } from "../../../Config/Constants";
import {useSnackbar} from "notistack";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yTabProps(index: number) {
  return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
  };
}

type TicketFormProps = {
    ticketId?: number | null,
    alarm?: Alarm | null,
    handleChange: Function,
    handleIsTicketClosed?: Function
}

export function TicketFormContainer({ticketId, alarm, handleChange: handleChangeOwner, handleIsTicketClosed}: TicketFormProps) {
    // Tabs
    const [tabValue, setTabValue] = useState<number>(0);
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    // Ticket
    const {data: findById} = FindByIdTicket(ticketId);
    const {data: findTicketAlarms} = FindTicketAlarms(ticketId);
    const [ticket, setTicket] = useState<Ticket>(new Ticket());
    const [ticketAlarms, setTicketAlarms] = useState<Array<Alarm>>([]);
    const [isTicketClosed, setIsTicketClosed] = useState<boolean>(false);

    const handleChangeAlarms = (alarms: Array<Alarm>) => {
        const data: Ticket = {
            ...ticket,
            alarmIds: alarms.map((alarm) => Number(alarm.id))
        };

        setTicket(data);
        setTicketAlarms(alarms);
    };

    useEffect(() => {
        const data = findById ? findById.data : new Ticket();
        const arrAlarms = new Array<Alarm>();

        // is ticket closed
        setIsTicketClosed(data.status === "CLOSED");

        // add alarm default
        if (alarm) {
            arrAlarms.push(alarm);

            // set default alarm: powerstation, skid and equipment
            data.powerStationId = alarm.powerStationId;
            data.skidId = alarm.skidId;
            data.equipmentId = alarm.equipmentId;
        }

        // find and add all ticket alarms, except aalarm defaul
        if (findTicketAlarms?.data) {
            arrAlarms.push(...findTicketAlarms.data.filter((el: Alarm) => !alarm || Number(alarm.id) !== el.id));
        }

        // set ticket alarms
        setTicket({...data, alarmIds: arrAlarms.map((alarm) => alarm.id)});
        setTicketAlarms(arrAlarms);
    }, [alarm, findById, findTicketAlarms, handleIsTicketClosed]);

    useEffect(() => handleChangeOwner(ticket), [handleChangeOwner, ticket]);
    
    useEffect(() => {
        if (handleIsTicketClosed) handleIsTicketClosed(isTicketClosed);
    }, [handleIsTicketClosed, isTicketClosed]);

    return (
        <Card sx={{
            width: '100%',
            height: '100%',
        }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
                <Tab label="Dados"   {...a11yTabProps(0)} />
                <Tab label="Alarmes" {...a11yTabProps(1)} />
            </Tabs>

            {/* Dados */}
            <CustomTabPanel value={tabValue} index={0}>
                <TicketFields ticket={ticket} isTicketClosed={isTicketClosed} handleChange={setTicket} />
            </CustomTabPanel>

            {/* Alarmes */}
            <CustomTabPanel value={tabValue} index={1}>
                <TicketAlarms alarm={alarm || null} ticketAlarms={ticketAlarms} isTicketClosed={isTicketClosed} handleChangeAlarms={handleChangeAlarms} />
            </CustomTabPanel>
        </Card>
    )
}

type TicketFormParam = {
    id?: number
};

export const isTicketValidForCreation = (ticket: Ticket): boolean => {
    return (
        ticket.description != null &&
        ticket.description.trim().length > 0 &&

        ticket.subject != null &&
        ticket.subject.trim().length > 0 &&

        ticket.type != null &&
        ticket.type.trim().length > 0 &&

        ticket.categoryId != null &&
        ticket.subCategoryId != null &&
        ticket.powerStationId != null &&
        ticket.scheduling != null
    );
}

const TicketForm = ({id}: TicketFormParam) => {
    const navigate = useNavigate();
    const account = useAppSelector(state => state.authentication.account);
    const openTicket = OpenTicket();
    const updateTicket = UpdateTicket();
    const [ticket, setTicket] = useState<Ticket>();
    const [isTicketClosed, setIsTicketClosed] = useState<boolean>(false);
    const { enqueueSnackbar } = useSnackbar();

    const handleSave = () => {
        if (ticket === undefined) return;

        if(!isTicketValidForCreation(ticket)){
            enqueueSnackbar(`Erro ao abrir ticket: Existem campos obrigatórios não preenchidos`, { variant: "error", autoHideDuration: 1500 });
            return
        }

        const opts = {
            onSuccess: () => navigate(SystemRoutes.SETTINGS_TICKET)
        };
        if (ticket.id) {
            updateTicket.mutate(ticket, opts);
        } else {
            openTicket.mutate(ticket, opts);
        }
    };
    const isAuthorizedToTicketSave = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, 
        AUTHORITIES.SAVE_TICKET
    ]);

    const isAuthorizedToTicketSearch = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_TICKET, AUTHORITIES.VIEW_TICKET
    ]);

    const isAuthorizedToTicketCategorySearch = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_TICKET_CATEGORY, AUTHORITIES.VIEW_TICKET_CATEGORY
    ]);

    const isAuthorizedToTicketSubCategorySearch = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SEARCH_TICKET_SUBCATEGORY, AUTHORITIES.VIEW_TICKET_SUBCATEGORY
    ]);

    const isAuthorizedToView = () => {
        if(isAuthorizedToTicketSave && isAuthorizedToTicketSearch && isAuthorizedToTicketCategorySearch && isAuthorizedToTicketSubCategorySearch ){
            return true;
        }
        return false;
    
    }

    return (
        <>
        <HeaderMenu isOnSave={true} 
            disableSave={isTicketClosed && !isAuthorizedToView()}
            handleSave={handleSave} systemRoutes={SystemRoutes.SETTINGS_TICKET}/>
        <Box style={{marginTop: 10}}>
            <TicketFormContainer ticketId={id} handleChange={setTicket} handleIsTicketClosed={setIsTicketClosed} />
        </Box>
        </>
    );
};

export default function TicketFormPage() {
    const queryClient = new QueryClient();
    const location = useLocation();
    const id = location.state?.id as number;

    return (
        <QueryClientProvider client={queryClient}>
            <TicketForm id={id} />
        </QueryClientProvider>
    )
}